import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import useIsBlueberryFeature from '../../scanDataApi/hooks/useIsBlueberryFeature';
import useScanData from '../../scanDataApi/hooks/useScanData';
import { ScanData } from '../../scanDataApi/scanDataDefinitions';
import {
  ResetItemsEnum,
  resetSelectFields,
  selectCurrPointId,
  selectCurrRowsideId,
  selectCurrScanID,
  setCurrPointId,
  setCurrRowNum,
  setCurrRowsideId,
  setCurrViewDirection,
} from '../../select/selectSlice';

/**
 * This hook will pick a point automatically if one is not selected when new data is loaded so that the image viewer is not blank.
 * It will also set the current fields (rowNum, rowsideId, viewDirection, pointId) if they are not set.
 */
function useImagePointSelection() {
  const dispatch = useAppDispatch();
  const { scanData, blueberryData } = useScanData();
  const currScanID = useAppSelector(selectCurrScanID);
  const currPointId = useAppSelector(selectCurrPointId);
  const currRowsideId = useAppSelector(selectCurrRowsideId);
  const isBlueberry = useIsBlueberryFeature();

  // this effect will select the first point in the current rowside set the corresponding fields as current (rowNum, rowsideId, viewDirection, pointId).  This is useful when clicking on a rowside in the bar chart so it jumps to the first point in the rowside.  If no rowside was selected such as when loading a scan for the first time, it will pick the first point in the entire scan.  It will also reset the currPointId if it is not in the data such as when the scan is changed to avoid stale data.
  useEffect(() => {
    const currentData = isBlueberry ? blueberryData : scanData;

    if (currScanID && currentData.data && currentData?.data.length > 0) {
      // if currPointId is not in the data, reset it to empty bc it may be stale data from another scan.  This will trigger the below logic.
      if (
        currPointId &&
        !currentData.data.find((point) => point.point_id === currPointId)
      ) {
        dispatch(resetSelectFields([ResetItemsEnum.PointId]));
      }

      // if no point is selected, select the first point in the current rowside if there is one, otherwise select the first point in the entire scan
      if (!currPointId) {
        const firstPoint = currentData.data.find(
          (point) => point.rowside_id === currRowsideId
        );

        // if there is a point in the current rowside, select it, otherwise select the first point in the entire scan
        let newPoint: ScanData | undefined;
        if (firstPoint) {
          newPoint = firstPoint;
        } else {
          newPoint = currentData.data[0];
        }

        // Update the fields with the new point info
        if (newPoint) {
          dispatch(setCurrRowNum(newPoint.rowside_num));
          dispatch(setCurrRowsideId(newPoint.rowside_id));
          dispatch(setCurrViewDirection(newPoint.view_direction));
          dispatch(setCurrPointId(newPoint.point_id));
        }
      }
    } else {
      // if there is no scan data, reset the fields
      dispatch(
        resetSelectFields([
          // ResetItemsEnum.PointId, // this interferes with loading the point from the url params
          ResetItemsEnum.RowNum,
          ResetItemsEnum.RowsideId,
          ResetItemsEnum.ViewDirection,
        ])
      );
    }
  }, [
    dispatch,
    currScanID,
    scanData,
    blueberryData,
    currRowsideId,
    currPointId,
    isBlueberry,
  ]);
}

export default useImagePointSelection;
